import { Component, Input, OnChanges, OnInit, SimpleChanges } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { Observable } from "rxjs";
import { BaseSettingsSectionComponent } from "../base-settings-section.component";
import { ExtensionSettingsService } from "../extension-settings.service";

@Component({
    selector: "app-typing-style-settings",
    templateUrl: "./typing-style-settings.component.html",
    styleUrls: ["./typing-style-settings.component.scss"],
    standalone: false
})
export class TypingStyleSettingsComponent extends BaseSettingsSectionComponent implements OnInit, OnChanges {
  @Input() initialTypingStyle: "letter" | "all" = "letter";
  @Input() initialTypingSpeed = 45;

  private lastSavedStyle: string;
  private lastSavedSpeed: number;

  constructor(
    private readonly fb: FormBuilder,
    private readonly settingsService: ExtensionSettingsService
  ) {
    super();
  }

  ngOnInit(): void {
    this.lastSavedStyle = this.initialTypingStyle;
    this.lastSavedSpeed = this.initialTypingSpeed;
    super.ngOnInit();
    this.updateTypingSpeedDisabledState();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.initialTypingStyle && !changes.initialTypingStyle.firstChange) {
      this.lastSavedStyle = changes.initialTypingStyle.currentValue;
      if (this.form) {
        this.form.patchValue({ typingStyle: this.lastSavedStyle }, { emitEvent: false });
        this.markPristine();
      }
    }
    if (changes.initialTypingSpeed && !changes.initialTypingSpeed.firstChange) {
      this.lastSavedSpeed = changes.initialTypingSpeed.currentValue;
      if (this.form) {
        this.form.patchValue({ typingSpeed: this.lastSavedSpeed }, { emitEvent: false });
        this.markPristine();
      }
    }
    if (changes.adminOverride && this.form) {
      this.updateTypingSpeedDisabledState();
    }
  }

  // 04-component-gaps-and-issues.md §3.3: the native <input type="range"> can't take
  // [disabled] bound alongside formControlName (reactive-forms restriction on native
  // form elements) — the admin-override disable moves to the FormControl itself.
  private updateTypingSpeedDisabledState(): void {
    const control = this.form?.get("typingSpeed");
    if (!control) return;
    if (this.adminOverride) {
      control.disable({ emitEvent: false });
    } else {
      control.enable({ emitEvent: false });
    }
  }

  buildForm(): FormGroup {
    return this.fb.group({
      typingStyle: [this.initialTypingStyle],
      typingSpeed: [this.initialTypingSpeed],
    });
  }

  getSaveObservable(): Observable<any> {
    // getRawValue() (not .value) — typingSpeed may be a disabled FormControl
    // (adminOverride), and .value omits disabled controls entirely.
    const { typingSpeed, typingStyle } = this.form.getRawValue();
    this.lastSavedStyle = typingStyle;
    this.lastSavedSpeed = typingSpeed;
    return this.settingsService.saveTypingSettings(typingSpeed, typingStyle, false);
  }

  resetToLastSaved(): void {
    this.form.patchValue(
      { typingStyle: this.lastSavedStyle, typingSpeed: this.lastSavedSpeed },
      { emitEvent: false }
    );
  }

  setDefault(): void {
    this.form.patchValue({ typingSpeed: 45 });
    this.settingsService.saveTypingSettings(45, this.form.value.typingStyle, true).subscribe(() => {
      this.lastSavedSpeed = 45;
      this.lastSavedStyle = this.form.value.typingStyle;
      this.markPristine();
    });
  }

  get typingStyle(): string {
    return this.form?.get("typingStyle")?.value;
  }

  get typingSpeed(): number {
    return this.form?.get("typingSpeed")?.value;
  }
}
